/**
 * Centralized breakpoint constants for responsive design
 *
 * CSS media queries cannot use JavaScript variables, so when updating these values,
 * also update the corresponding CSS files:
 * - App.css (@media min-width: 1100px)
 * - AIChatPanel.css (@media max-width: 1100px)
 * - CommentGutter.css (@media max-width: 1100px)
 * - CommentSlidePanel.css (@media min-width: 1101px)
 * - SelectionToolbar.css (@media min-width: 1100px)
 */

// Desktop breakpoint - AI panel, comment gutter show/hide
export const BREAKPOINT_DESKTOP = 1100;

/**
 * Reading focus position - unified position for focus zone, guideline, auto-read scan line,
 * and keyboard navigation scroll target.
 *
 * When updating this value, also update the CSS variable in themes.css:
 * --reading-focus-position: 40vh
 *
 * Used by:
 * - FocusGuideDropdown.css (blur overlays and guideline position)
 * - ReadingContext.jsx (auto-read scan line)
 * - ReaderContent.jsx (keyboard navigation scroll position)
 */
export const READING_FOCUS_POSITION = 0.4; // 40% of viewport height

// Tablet breakpoint - general mobile/tablet transition
export const BREAKPOINT_TABLET = 768;

// Small mobile breakpoint
export const BREAKPOINT_MOBILE = 480;

/**
 * Check if current viewport is desktop width (> BREAKPOINT_DESKTOP)
 * @returns {boolean}
 */
export const isDesktop = () => {
  if (typeof window === 'undefined') return true; // SSR fallback
  return window.innerWidth > BREAKPOINT_DESKTOP;
};

/**
 * Check if current viewport is mobile/tablet width (<= BREAKPOINT_DESKTOP)
 * @returns {boolean}
 */
export const isMobile = () => {
  if (typeof window === 'undefined') return false; // SSR fallback
  return window.innerWidth <= BREAKPOINT_DESKTOP;
};

/**
 * Check if current viewport is small mobile width (<= BREAKPOINT_TABLET)
 * @returns {boolean}
 */
export const isSmallMobile = () => {
  if (typeof window === 'undefined') return false; // SSR fallback
  return window.innerWidth <= BREAKPOINT_TABLET;
};

/**
 * Minimum width of the content rail (the `.app-main` box itself).
 *
 * As the viewport narrows the shell gives chrome back to the content in order:
 * the expanded sidebar first, then the open right panel. Enforced by
 * useContentWidthGuard and floored in CSS.
 *
 * When updating this value, also update the CSS variable in App.css:
 * --content-min-width: 640px
 */
export const MIN_CONTENT_WIDTH = 640;

/** Docked sidebar width — mirrors --sidebar-width in App.css. */
export const SIDEBAR_WIDTH = 220;

/** Collapsed desktop sidebar width — mirrors --sidebar-collapsed-width in App.css. */
export const SIDEBAR_COLLAPSED_WIDTH = 64;

/** Soft-frame shell gap — mirrors --app-shell-gap (--rds-space-3) in App.css. */
export const SHELL_GAP = 12;

/** Minimum content rail width that still permits a docked right panel. */
export const RIGHT_PANEL_DOCK_MIN_CONTENT_WIDTH = 720;

/**
 * Mobile shell threshold — bottom tab bar, sheet-based disclosure, edge-to-edge
 * content. Mirrors the `max-width: 768px` shell branches in App.css.
 */
export const MOBILE_SHELL_MAX_WIDTH = BREAKPOINT_TABLET;

/**
 * Single owner of the "render the phone shell" decision, so phone web and the
 * Capacitor client ship one configuration instead of two.
 *
 * Native always resolves to the phone shell: the native targets are phone
 * builds, and a tablet running them gets the phone chrome rather than a
 * desktop shell it has no pointer for.
 */
export const usesMobileShell = ({ viewportWidth, isNative = false }) =>
  isNative || viewportWidth <= MOBILE_SHELL_MAX_WIDTH;

/**
 * Content width left after the desktop shell reserves both side rails.
 */
export const getDockedContentWidth = ({ viewportWidth, panelWidth, sidebarExpanded }) =>
  viewportWidth
  - (sidebarExpanded ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH)
  - panelWidth
  - SHELL_GAP;

/**
 * Shared right-panel mode, in three forms rather than two.
 *
 * `docked` and `floating` both rest on the same desktop promise — the content
 * stays visible while you ask about it. A desktop viewport alone is not enough
 * to dock: the resulting content rail must also stay above responsive reflow.
 *
 * On the phone shell that promise cannot be kept at all, because any surface
 * wide enough to read an answer in covers the text. So the panel becomes
 * `page`: a full-height surface pushed onto the current screen's own stack,
 * carrying its subject in the header. The decision stays here so feature
 * screens never invent a fourth form or a second threshold.
 */
export const getRightPanelLayout = ({
  viewportWidth,
  panelWidth,
  sidebarExpanded,
  isNative = false,
}) => {
  if (usesMobileShell({ viewportWidth, isNative })) return 'page';

  return viewportWidth > BREAKPOINT_DESKTOP
    && getDockedContentWidth({ viewportWidth, panelWidth, sidebarExpanded })
      >= RIGHT_PANEL_DOCK_MIN_CONTENT_WIDTH
    ? 'docked'
    : 'floating';
};
